import { create } from 'zustand'
import type { Card, CardWithContext, CardTreeNode } from '@/types'
import { api } from '@/services/api'
import {
  findDirectChildren,
  findSiblings,
  getParentLid,
  buildLidTree,
  sortLids,
  type LidTreeNode,
} from '@/utils/lid'
import { hasReferenceToLid } from '@/utils/crossref'
import { useTopicsStore } from './topicsStore'

interface CardsState {
  cards: Map<string, Card>
  isLoading: boolean
  error: string | null
  selectedCardLid: string | null
}

export interface NavigationContext {
  parent: Card | null
  firstChild: Card | null
  prevSibling: Card | null
  nextSibling: Card | null
  children: Card[]
  siblings: Card[]
  backlinks: Card[]
}

interface CardsActions {
  // Local state actions
  setCards: (cards: Card[]) => void
  setLoading: (isLoading: boolean) => void
  setError: (error: string | null) => void
  setSelectedCard: (lid: string | null) => void

  // API actions
  fetchCards: (topicId?: string) => Promise<void>
  fetchCard: (lid: string) => Promise<Card>
  addCard: (data: {
    topicId: string
    title: string
    content: string
    parentLid?: string | null
    branchType?: 'branch' | 'continue'
  }) => Promise<Card>
  updateCard: (lid: string, updates: { title?: string; content?: string }) => Promise<void>
  deleteCard: (lid: string) => Promise<void>

  // Inbox actions
  addInboxCard: (data: { title: string; content: string }) => Promise<Card>
  placeInboxCard: (lid: string, data: {
    topicId: string
    parentLid: string | null
    branchType: 'branch' | 'continue'
  }) => Promise<Card>

  // Computed getters
  getCard: (lid: string) => Card | undefined
  getCardsByTopic: (topicId: string) => Card[]
  getCardWithContext: (lid: string) => CardWithContext | null
  getNavigationContext: (lid: string) => NavigationContext | null
  getCardTree: (topicId: string) => CardTreeNode[]
  getAllLids: () => string[]
  getInboxCards: () => Card[]
  getInboxCount: () => number
}

type CardsStore = CardsState & CardsActions

export const useCardsStore = create<CardsStore>((set, get) => ({
  // Initial state - isLoading starts as true until first fetch completes
  cards: new Map(),
  isLoading: true,
  error: null,
  selectedCardLid: null,

  // Local state actions
  setCards: (cards) => {
    const cardsMap = new Map<string, Card>()
    cards.forEach((card) => cardsMap.set(card.lid, card))
    set({ cards: cardsMap, isLoading: false, error: null })
  },

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error, isLoading: false }),

  setSelectedCard: (lid) => set({ selectedCardLid: lid }),

  // API actions
  fetchCards: async (topicId?: string) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.getCards(topicId)
      const cardsMap = new Map<string, Card>(get().cards)
      const cards = response?.cards ?? []
      cards.forEach((card) => cardsMap.set(card.lid, card))
      set({ cards: cardsMap, isLoading: false })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch cards'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  fetchCard: async (lid: string) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.getCard(lid)
      const cardsMap = new Map<string, Card>(get().cards)
      cardsMap.set(response.card.lid, response.card)
      set({ cards: cardsMap, isLoading: false })
      return response.card
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch card'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  addCard: async (data) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.createCard(data)
      const cardsMap = new Map<string, Card>(get().cards)
      cardsMap.set(response.card.lid, response.card)
      set({ cards: cardsMap, isLoading: false })
      // Update total card count (non-inbox cards only)
      if (data.topicId !== 'inbox') {
        useTopicsStore.getState().incrementTotalCards(1)
      }
      return response.card
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create card'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  updateCard: async (lid, updates) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.updateCard(lid, updates)
      const cardsMap = new Map<string, Card>(get().cards)
      cardsMap.set(response.card.lid, response.card)
      set({ cards: cardsMap, isLoading: false })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update card'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  deleteCard: async (lid) => {
    set({ isLoading: true, error: null })
    try {
      const card = get().cards.get(lid)
      await api.deleteCard(lid)
      const cardsMap = new Map<string, Card>(get().cards)
      cardsMap.delete(lid)
      set({ cards: cardsMap, isLoading: false })
      // Update total card count (non-inbox cards only)
      if (card && card.topicId !== 'inbox') {
        useTopicsStore.getState().incrementTotalCards(-1)
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete card'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  // Inbox actions
  addInboxCard: async (data) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.createInboxCard(data)
      const cardsMap = new Map<string, Card>(get().cards)
      cardsMap.set(response.card.lid, response.card)
      set({ cards: cardsMap, isLoading: false })
      return response.card
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create inbox card'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  placeInboxCard: async (lid, data) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.placeInboxCard(lid, data)
      const cardsMap = new Map<string, Card>(get().cards)
      // Remove old inbox card
      cardsMap.delete(lid)
      // Add new placed card
      cardsMap.set(response.card.lid, response.card)
      set({ cards: cardsMap, isLoading: false })
      // Update total card count (inbox card is now a real card)
      useTopicsStore.getState().incrementTotalCards(1)
      return response.card
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to place card'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  // Computed getters
  getCard: (lid) => get().cards.get(lid),

  getCardsByTopic: (topicId) => {
    const cards = get().cards
    return Array.from(cards.values()).filter((card) => card.topicId === topicId)
  },

  getCardWithContext: (lid) => {
    const cards = get().cards
    const card = cards.get(lid)
    if (!card) return null

    const allLids = Array.from(cards.keys())

    // Get parent
    const parentLid = getParentLid(lid)
    const parent = parentLid ? cards.get(parentLid) || null : null

    // Get children
    const childLids = findDirectChildren(lid, allLids)
    const children = childLids.map((l) => cards.get(l)).filter((c): c is Card => c !== undefined)

    // Get siblings
    const siblingLids = findSiblings(lid, allLids).filter((l) => l !== lid)
    const siblings = siblingLids.map((l) => cards.get(l)).filter((c): c is Card => c !== undefined)

    // Get backlinks (cards that reference this card)
    const backlinks = Array.from(cards.values()).filter(
      (c) => c.lid !== lid && hasReferenceToLid(c.content, lid)
    )

    return {
      ...card,
      parent,
      children,
      siblings,
      backlinks,
    }
  },

  getNavigationContext: (lid) => {
    const cards = get().cards
    const card = cards.get(lid)
    if (!card) return null

    const allLids = Array.from(cards.keys())

    // Get parent
    const parentLid = getParentLid(lid)
    const parent = parentLid ? cards.get(parentLid) || null : null

    // Get children (sorted)
    const childLids = sortLids(findDirectChildren(lid, allLids))
    const children = childLids.map((l) => cards.get(l)).filter((c): c is Card => c !== undefined)
    const firstChild = children.length > 0 ? children[0] : null

    // Get siblings (sorted, including current)
    const siblingLids = sortLids(findSiblings(lid, allLids))
    const siblings = siblingLids
      .filter((l) => l !== lid)
      .map((l) => cards.get(l))
      .filter((c): c is Card => c !== undefined)

    // Find prev/next sibling
    const currentIndex = siblingLids.indexOf(lid)
    const prevSiblingLid = currentIndex > 0 ? siblingLids[currentIndex - 1] : null
    const nextSiblingLid = currentIndex < siblingLids.length - 1 ? siblingLids[currentIndex + 1] : null
    const prevSibling = prevSiblingLid ? cards.get(prevSiblingLid) || null : null
    const nextSibling = nextSiblingLid ? cards.get(nextSiblingLid) || null : null

    // Get backlinks
    const backlinks = Array.from(cards.values()).filter(
      (c) => c.lid !== lid && hasReferenceToLid(c.content, lid)
    )

    return {
      parent,
      firstChild,
      prevSibling,
      nextSibling,
      children,
      siblings,
      backlinks,
    }
  },

  getCardTree: (topicId) => {
    const topicCards = get().getCardsByTopic(topicId)
    const lids = topicCards.map((c) => c.lid)
    const lidTree = buildLidTree(lids)

    // Convert to CardTreeNode with actual card data
    const convertNode = (node: LidTreeNode): CardTreeNode | null => {
      const card = get().cards.get(node.lid)
      if (!card) return null

      return {
        card,
        children: node.children.map(convertNode).filter((n): n is CardTreeNode => n !== null),
      }
    }

    return lidTree.map(convertNode).filter((n): n is CardTreeNode => n !== null)
  },

  getAllLids: () => Array.from(get().cards.keys()),

  getInboxCards: () => {
    const cards = get().cards
    return Array.from(cards.values()).filter((card) => card.topicId === 'inbox')
  },

  getInboxCount: () => {
    const cards = get().cards
    return Array.from(cards.values()).filter((card) => card.topicId === 'inbox').length
  },
}))
